import {
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { ImpersonationService } from '../../services/impersonation.service';
import { ConfirmDialogComponent } from '../../../../services/confirm-dialog.component';

const COLLAPSED_STORAGE_KEY = 'impersonation-banner-collapsed';

@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  template: `
    <div
      class="impersonation-banner"
      [class.impersonation-banner--collapsed]="collapsed()"
      *ngIf="impersonation.isImpersonating()"
    >
      <ng-container *ngIf="!collapsed(); else collapsedFab">
        <button
          type="button"
          class="impersonation-banner__toggle"
          aria-expanded="true"
          aria-label="Collapse impersonation banner"
          (click)="toggleCollapsed()"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        <div class="impersonation-banner__text">
          <span class="impersonation-banner__target">{{ impersonation.bannerTargetLabel() }}</span>
          <span class="impersonation-banner__sep" aria-hidden="true">·</span>
          <span class="impersonation-banner__actor">{{ impersonation.bannerActorLabel() }}</span>
          <span class="impersonation-banner__expires" *ngIf="impersonation.bannerExpiresAt()">
            · {{ impersonation.bannerExpiresAt() }}
          </span>
        </div>
        <button type="button" class="impersonation-banner__exit" (click)="confirmExit()">
          Exit impersonation
        </button>
      </ng-container>
      <ng-template #collapsedFab>
        <button
          type="button"
          class="impersonation-banner__fab"
          title="Impersonating — click for details"
          aria-expanded="false"
          aria-label="Impersonating. Expand for details."
          (click)="toggleCollapsed()"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </button>
      </ng-template>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .impersonation-banner {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
        padding: 0.75rem 1.25rem;
        background: #1e3a5f;
        color: #fff;
        box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.18);
      }
      .impersonation-banner--collapsed {
        left: 1rem;
        right: auto;
        bottom: 1rem;
        width: auto;
        padding: 0;
        background: transparent;
        box-shadow: none;
      }
      .impersonation-banner__fab {
        width: 2.5rem;
        height: 2.5rem;
        border: 0;
        border-radius: 50%;
        background: #1e3a5f;
        color: #fff;
        box-shadow: 0 2px 10px rgba(30, 58, 95, 0.35);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.95rem;
      }
      .impersonation-banner__fab:hover {
        background: #274a75;
      }
      .impersonation-banner__toggle {
        border: 1px solid rgba(255, 255, 255, 0.35);
        background: transparent;
        color: #fff;
        border-radius: 6px;
        width: 2rem;
        height: 2rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
      }
      .impersonation-banner__toggle:hover {
        background: rgba(255, 255, 255, 0.12);
      }
      .impersonation-banner__text {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.35rem 0.5rem;
        font-size: 0.9rem;
        flex: 1;
      }
      .impersonation-banner__target {
        font-weight: 700;
      }
      .impersonation-banner__actor {
        font-weight: 500;
        opacity: 0.95;
      }
      .impersonation-banner__expires {
        opacity: 0.85;
        font-size: 0.85rem;
      }
      .impersonation-banner__sep {
        opacity: 0.5;
      }
      .impersonation-banner__exit {
        border: 1px solid rgba(255, 255, 255, 0.45);
        background: transparent;
        color: #fff;
        border-radius: 6px;
        padding: 0.35rem 0.75rem;
        font-weight: 600;
        cursor: pointer;
      }
      .impersonation-banner__exit:hover {
        background: rgba(255, 255, 255, 0.12);
      }
      :host ::ng-deep .impersonation-exit-confirm {
        width: 480px !important;
        max-width: 92vw !important;
      }
    `,
  ],
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  readonly impersonation = inject(ImpersonationService);
  private readonly dialog = inject(MatDialog);
  readonly collapsed = signal(this.readCollapsed());

  constructor() {
    effect(() => {
      this.syncBodyPadding(this.impersonation.isImpersonating(), this.collapsed());
    });
  }

  ngOnInit(): void {
    // Auth may consume the handoff after this service is constructed.
    this.impersonation.refreshFromStorage();
  }

  ngOnDestroy(): void {
    this.syncBodyPadding(false, false);
  }

  toggleCollapsed(): void {
    const next = !this.collapsed();
    this.collapsed.set(next);
    this.persistCollapsed(next);
  }

  confirmExit(): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '480px',
        maxWidth: '92vw',
        panelClass: 'impersonation-exit-confirm',
        data: {
          title: 'Exit impersonation',
          message:
            'End this impersonation session and return to Way Superuser? Unsaved changes in this tab may be lost.',
          accept: 'Exit',
          reject: 'Cancel',
          acceptButtonStyleClass: 'btn-danger',
          rejectButtonStyleClass: 'btn-text',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.impersonation.exitImpersonation();
        }
      });
  }

  private readCollapsed(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    try {
      return sessionStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private persistCollapsed(collapsed: boolean): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }

  private syncBodyPadding(active: boolean, collapsed: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.style.paddingBottom = active && !collapsed ? '3.5rem' : '';
  }
}
